// Pure structure-traversal helpers for the posting-template editor.
//
// The atts/spec `structure` is a tree of category containers holding attribute
// components. These helpers walk that tree to (a) map assignment_id →
// attribute_path, (b) collect every container assignment_id (for expand/collapse
// defaults), and (c) decorate mappings with their attribute_path. Extracted from
// the edit page; pure functions of their inputs (no React).

import type { AttributeStructure } from '@/endpoints/channel/getAttributeSpec';
import type { PostingTemplateMapping, TemplateEditorCategory } from '@/types/postingTemplate';

/** Map each mappable attribute's assignment_id to its attribute_path. */
export const buildAssignmentToPathLookup = (structure: Partial<AttributeStructure>): Record<string, string> => {
  const lookup: Record<string, string> = {};
  const traverseStructure = (component: TemplateEditorCategory) => {
    if (component.containerComponents) {
      component.containerComponents.forEach(traverseStructure);
    }
    if (component.attributeComponents) {
      component.attributeComponents.forEach((attribute) => {
        if (attribute.assignment_id && attribute.attribute_path) {
          lookup[attribute.assignment_id] = attribute.attribute_path;
        }
      });
    }
  };
  if (structure.containerComponents) {
    structure.containerComponents.forEach(traverseStructure);
  }
  return lookup;
};

/** Collect every category container's assignment_id (used to expand all by default). */
export const getAllContainerIds = (structure: Partial<AttributeStructure>): Set<number> => {
  const containerIds = new Set<number>();
  const traverseStructure = (component: TemplateEditorCategory) => {
    // Categories use assignment_id for tracking expansion.
    if (component.assignment_id) {
      containerIds.add(component.assignment_id);
    }
    if (component.containerComponents) {
      component.containerComponents.forEach(traverseStructure);
    }
  };
  if (structure && structure.containerComponents) {
    structure.containerComponents.forEach(traverseStructure);
  }
  return containerIds;
};

/** Stamp each mapping with its attribute_path resolved from the structure. */
export const decorateMappingsWithAttributePath = (
  mappings: PostingTemplateMapping[],
  structure: Partial<AttributeStructure>
): PostingTemplateMapping[] => {
  const assignmentToPathLookup = buildAssignmentToPathLookup(structure);

  return mappings.map((mapping) => {
    const attributePath = assignmentToPathLookup[mapping.assignment_id];
    return {
      ...mapping,
      attribute_path: attributePath || null,
    };
  });
};
